import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 32px;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
`;

const List = styled.ul`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
  padding-left: 24px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const ContactInfo = styled.div`
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 32px;
`;

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ContactText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 8px;
`;

const PrivacyPolicyPage = () => {
  return (
    <PageContainer>
      <SEO 
        title="Privacy Policy"
        description="Read TheMadZone's Privacy Policy to understand how we collect, use, and protect your personal information. Learn about your privacy rights and data protection."
        keywords="privacy policy, data protection, personal information, TheMadZone privacy, GDPR"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

        <Section>
          <Paragraph>
            At TheMadZone, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and make purchases from us.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          
          <Paragraph>
            <strong>Personal Information:</strong> When you create an account, place an order, or contact us, we may collect:
          </Paragraph>
          <List>
            <ListItem>Name and contact information (email address, phone number)</ListItem>
            <ListItem>Billing and shipping addresses</ListItem>
            <ListItem>Payment information (processed securely through our payment providers)</ListItem>
            <ListItem>Date of birth (for age verification where required)</ListItem>
            <ListItem>Account credentials (password, stored securely)</ListItem>
          </List>

          <Paragraph>
            <strong>Automatically Collected Information:</strong> When you visit our website, we automatically collect:
          </Paragraph>
          <List>
            <ListItem>IP address and device information</ListItem>
            <ListItem>Browser type and version</ListItem>
            <ListItem>Pages visited and time spent on pages</ListItem>
            <ListItem>Referring website addresses</ListItem>
            <ListItem>Cookies and similar tracking technologies</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          
          <Paragraph>We use the information we collect to:</Paragraph>
          <List>
            <ListItem>Process and fulfill your orders</ListItem>
            <ListItem>Communicate with you about your orders, account, and our services</ListItem>
            <ListItem>Send you marketing communications (with your consent)</ListItem>
            <ListItem>Improve our website and customer experience</ListItem>
            <ListItem>Prevent fraud and ensure security</ListItem>
            <ListItem>Comply with legal obligations</ListItem>
            <ListItem>Administer our referral and rewards programs</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Information Sharing and Disclosure</SectionTitle>
          
          <Paragraph>
            We do not sell your personal information. We may share your information with:
          </Paragraph>
          <List>
            <ListItem><strong>Service Providers:</strong> Third-party companies that help us operate our business (payment processors, shipping companies, email service providers)</ListItem>
            <ListItem><strong>Legal Requirements:</strong> When required by law or to protect our rights</ListItem>
            <ListItem><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</ListItem>
          </List>
          
          <Paragraph>
            All third-party service providers are required to maintain the confidentiality of your information and use it only for the purposes we specify.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Data Security</SectionTitle>
          
          <Paragraph>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Cookies and Tracking Technologies</SectionTitle>
          
          <Paragraph>
            We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookies through your browser settings, but disabling cookies may affect website functionality.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Your Rights</SectionTitle>
          
          <Paragraph>Under UK data protection laws, you have the right to:</Paragraph>
          <List>
            <ListItem>Access your personal information</ListItem>
            <ListItem>Correct inaccurate or incomplete information</ListItem>
            <ListItem>Request deletion of your personal information</ListItem>
            <ListItem>Object to processing of your personal information</ListItem>
            <ListItem>Request restriction of processing</ListItem>
            <ListItem>Data portability</ListItem>
            <ListItem>Withdraw consent where processing is based on consent</ListItem>
          </List>
          
          <Paragraph>
            To exercise these rights, please contact us at <strong>hello@themadzone.com</strong>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Data Retention</SectionTitle>
          
          <Paragraph>
            We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Children's Privacy</SectionTitle>
          
          <Paragraph>
            Our website is not intended for children under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. International Data Transfers</SectionTitle>
          
          <Paragraph>
            Your information may be transferred to and processed in countries outside the UK and European Economic Area (EEA). We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Changes to This Privacy Policy</SectionTitle>
          
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.
          </Paragraph>
        </Section>

        <ContactInfo>
          <ContactTitle>Contact Us</ContactTitle>
          <ContactText>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </ContactText>
          <ContactText>
            <strong>Email:</strong> hello@themadzone.com
          </ContactText>
          <ContactText>
            <strong>Address:</strong><br />
            Apartment 2, 45-47 Savile Court<br />
            Hull, England, HU1 3BT<br />
            United Kingdom
          </ContactText>
          <ContactText>
            <strong>Company:</strong> TheMadZone
          </ContactText>
          <ContactText>
            <strong>Company Number:</strong> THEMADZONE LTD - Company number 16470350
          </ContactText>
        </ContactInfo>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;

